import dynamic from 'next/dynamic';

import BylineList from 'components/Byline';
import Label from 'components/Globals/Label';
import SocialShareDropdown from 'components/SocialShareDropdown';
import type { GridAuthorWithUri } from 'interfaces/content/Ranking';
import type { Author } from 'interfaces/content/Authors';
import type { PostSettings } from 'interfaces/content/articles/Post';
import type { MediaItem } from 'interfaces/content/Media';
import RawHtml from 'utils/miscUtils/rawHtml';
import * as S from './HeroStyles';
import SponsorLogo from '../SponsorLogo';
import getCommonTestId from '@/main/constants/testsIds/common';

const FeaturedHeroMedia = dynamic(() => import('components/FeaturedHeroMedia'));

export interface HeroProps {
  authors?: Author[] | GridAuthorWithUri[];
  title: string;
  description: string;
  videoId: string | null;
  postSettings: PostSettings;
  imageCaption: string;
  imageCredit: string;
  templateType: string;
  featuredMediaType: string;
  partnerLogo: MediaItem;
  partnerLogoLink: string;
  fortuneLogo?: MediaItem;
  paidContentLabel: string;
  hideAuthors?: boolean;
}

const Hero = ({
  title,
  description,
  authors,
  videoId,
  postSettings,
  imageCaption,
  imageCredit,
  templateType,
  featuredMediaType,
  partnerLogo,
  partnerLogoLink,
  fortuneLogo,
  paidContentLabel,
  hideAuthors,
}: HeroProps) => {
  if (templateType === 'custom-iframe') return null;

  return (
    <S.Wrapper>
      <S.Container className={templateType !== 'standard' ? 'has-featured-media' : ''}>
        {!hideAuthors && authors ? (
          <S.PaidContentAndLogo>
            <Label paidContentLabel={paidContentLabel} />
            <SponsorLogo
              partnerLogo={partnerLogo}
              partnerLogoLink={partnerLogoLink}
              fortuneLogo={fortuneLogo}
              className='separator'
            />
          </S.PaidContentAndLogo>
        ) : (
          <S.SocialLinksInHeaderRow>
            <S.PaidContentAndLogo>
              <Label paidContentLabel={paidContentLabel} />
              <SponsorLogo
                partnerLogo={partnerLogo}
                partnerLogoLink={partnerLogoLink}
                fortuneLogo={fortuneLogo}
                className='separator'
              />
            </S.PaidContentAndLogo>
            <SocialShareDropdown
              title=''
              className='byline-dropdown'
            />
          </S.SocialLinksInHeaderRow>
        )}

        {templateType !== 'standard' && (
          <S.FeaturedMediaContainer>
            <FeaturedHeroMedia
              featuredMediaType={featuredMediaType}
              imageCaption={imageCaption}
              imageCredit={imageCredit}
              postSettings={postSettings}
              videoId={videoId}
            />
          </S.FeaturedMediaContainer>
        )}

        <S.HeadingTitle
          as='h1'
          $fontFamily='saol'
          $size={{ default: 'level3', mdMin: 'level1' }}
          data-cy={getCommonTestId('BRANDED_ARTICLE_HERO_TITLE')}
        >
          {title}
        </S.HeadingTitle>

        {description && <S.Description>{description}</S.Description>}

        <S.ShareButtonWrapper>
          {!hideAuthors && authors && (
            <S.Byline>
              <BylineList
                bylineList={authors}
                description={postSettings?.bylineCommentary}
              />
              <SocialShareDropdown
                title=''
                className='byline-dropdown'
              />
            </S.Byline>
          )}
        </S.ShareButtonWrapper>
      </S.Container>

      {templateType === 'standard' && (
        <S.ImageContainer>
          <div data-cy={getCommonTestId('BRANDED_ARTICLE_HERO_IMAGE')}>
            <FeaturedHeroMedia
              featuredMediaType={featuredMediaType}
              imageCaption={imageCaption}
              imageCredit={imageCredit}
              postSettings={postSettings}
              videoId={videoId}
            />
          </div>
          <S.Source>{RawHtml({ html: postSettings.mediaItem.caption })}</S.Source>
        </S.ImageContainer>
      )}
    </S.Wrapper>
  );
};

export default Hero;
